/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import {
  AlertColor,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import {
  PeriodicJobFormFields,
  DEFAULT_JOB_NUMBER_LENGTH,
  SnackbarSeverity,
  velocitiOrganizationId,
  VELOCITI_VENDOR_NAME,
  VELOCITI_NUMBER,
  JobType,
  DebounceTime,
  DEFAULT_JOB_NUMBER_MAX_LENGTH,
} from '../../../../Shared/Constants/App';

import SnackbarMessage from '../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import { CardTitle, CloseDialogBoxIcon, buttonStyles } from '../UpdateShiftDate/UpdateShiftDate.Style';
import { DatePickerContainer } from '../JobDetails/OneTimeJobDetails.Style';
import theme from '../../../../Shared/Themes/theme';
import { Icons } from '../../../../Shared/Constants/Icons';
import { DialogWrapper } from '../../../Vendor/Components/CreateBid.Style';
import WorkOrdersApiService from '../../Services/WorkOrdersService';
import AllJobsApiService from '../../Services/JobManagementService';
import { ICreatePeriodicJobCard, JobFulfillmentType } from '../../Models/JobManagementDashboard.Model';
import { getStoredCustomerDetails } from '../../../../Shared/Utilities/utils';
import { BoxShadow } from '../../../../Shared/Constants/Style.Constants';

const CreatePeriodicJobCard = ({
  open,
  onClose,
  isLoading,
  selectedRows,
  handleVendorChange,
}: ICreatePeriodicJobCard): JSX.Element => {
  const { t } = useTranslation(['jobManagement', 'altTexts']);

  const [isJobNumberLoading, setIsJobNumberLoading] = useState(false);
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const [jobNumberError, setJobNumberError] = useState<string>('');
  const [hasExistingRecurring, setHasExistingRecurring] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>(SnackbarSeverity.success);
  const [selectedVendorData, setSelectedVendorData] = useState<any[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string | undefined>(undefined);
  const [isFulfillmentTypeOpen, setFulfillmentTypeOpen] = useState(false);
  const jobNumberTimerRef = useRef<number | null>(null);
  const isPrefilled = useRef<boolean>(false);
  const { confirmButton, buttonText, textField } = buttonStyles;

  const getFormattedVendor = (row: any) => {
    if (row?.vendorNumber && row?.vendorName) {
      return `${row?.vendorNumber} - ${row?.vendorName}`;
    }
    return row?.vendorName || '';
  };

  const getInitialFulfillmentType = (row: any) => {
    if (!row) return '';
    return row?.vendorId === velocitiOrganizationId
      ? JobFulfillmentType.SelfPerformed
      : JobFulfillmentType.SubContracted;
  };

  const { control, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      [PeriodicJobFormFields.JobNumber]: selectedRows?.[0]?.jobNumber || '',
      [PeriodicJobFormFields.FulfillmentType]: getInitialFulfillmentType(selectedRows?.[0]),
      [PeriodicJobFormFields.Vendor]: getFormattedVendor(selectedRows?.[0]),
    },
  });

  const { userId = '' } = getStoredCustomerDetails() || {};

  const fetchPeriodicJobs = async (data: any) => {
    const firstRow = selectedRows?.[0];
    try {
      const payload = {
        jobNumber: data[PeriodicJobFormFields.JobNumber] || firstRow?.jobNumber,
        vendorId: selectedVendorId || selectedVendorData[0]?.vendorId || firstRow?.vendorId,
        jobType: JobType.Periodic,
        jobStartDate: firstRow?.startDate || new Date(),
        vendorEmail: firstRow?.vendorEmail || '',
        selfPerformed: fulfillmentType === JobFulfillmentType.SelfPerformed,
        createdBy: userId || '',
        schedules: [],
        customerId: firstRow?.customerId || '',
      };

      setIsCreatingJob(true);
      const response = await WorkOrdersApiService.createPeriodicJob(payload);
      if (!response?.data || response?.data?.length === 0) {
        const errorMessage = String(response?.errors?.[0]?.message || t('SomeThingWentWrong:SomeThingWentWrong'));
        showSnackbar(SnackbarSeverity.error, errorMessage);
        setJobNumberError(errorMessage);
      } else {
        setJobNumberError('');
        showSnackbar(SnackbarSeverity.success, t('jobManagement:periodicJobCreatedSuccess'));
        onClose();
      }
      setIsCreatingJob(false);
    } catch (error) {
      console.error('error', error);
      showSnackbar(SnackbarSeverity.error, t('jobManagement:errorCreatingJob'));
      setIsCreatingJob(false);
    }
  };

  const jobNumber = watch(PeriodicJobFormFields.JobNumber);
  const fulfillmentType = watch(PeriodicJobFormFields.FulfillmentType) as JobFulfillmentType;
  const vendor = watch(PeriodicJobFormFields.Vendor);

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  const handleOpenVendorSelection = async () => {
    setValue(PeriodicJobFormFields.Vendor, '');
    setSelectedVendorData([]);
    setSelectedVendorId(undefined);
    const result = handleVendorChange(true);
    if (result && typeof (result as any).then === 'function') {
      try {
        const data = await result;
        if (data && data.length > 0) {
          setSelectedVendorData([data[0]]);
          setSelectedVendorId(String(data[0].vendorId));
          setValue(PeriodicJobFormFields.Vendor, data[0]?.vendorName || '');
        }
      } catch (error) {
        console.error('error', error);
      }
    }
  };

  const fetchExistingJobNumber = async (jobNumber: string) => {
    if (!jobNumber) return;
    try {
      setIsJobNumberLoading(true);
      const response = await AllJobsApiService.fetchWorkTickets(jobNumber);
      if (!response?.data || response?.data?.length === 0) {
        const message = String(response?.errors?.[0]?.message || t('jobManagement:errorFetchingExistingJob'));
        showSnackbar(SnackbarSeverity.error, message);
        setJobNumberError(message);
        setHasExistingRecurring(false);
      } else {
        const items: any[] = response?.data || [];
        const hasPeriodic = items.some(
          (it) => (it?.jobType || '').toString().toLowerCase() === JobType.Periodic.toLowerCase()
        );
        const hasRecurring = items.some(
          (it) => (it?.jobType || '').toString().toLowerCase() === JobType.Recurring.toLowerCase()
        );
        if (hasPeriodic) {
          const message = t('jobManagement:periodicJobExists');
          showSnackbar(SnackbarSeverity.error, message);
          setHasExistingRecurring(false);
          setJobNumberError(message);
        } else if (hasRecurring) {
          setHasExistingRecurring(true);
          setJobNumberError('');
        } else {
          const message = t('jobManagement:recurringJobNotFound');
          setHasExistingRecurring(false);
          setJobNumberError(message);
        }
      }
    } catch (err) {
      console.error('fetchExistingJobNumber error', err);
    } finally {
      setIsJobNumberLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRows?.[0]) {
      const firstRow = selectedRows[0];
      setValue(PeriodicJobFormFields.JobNumber, firstRow?.jobNumber);
      setValue(PeriodicJobFormFields.Vendor, getFormattedVendor(firstRow));
      setValue(PeriodicJobFormFields.FulfillmentType, getInitialFulfillmentType(firstRow));

      if (firstRow?.vendorId) {
        setSelectedVendorData([
          {
            vendorId: firstRow?.vendorId,
            vendorName: firstRow?.vendorName,
            vendorNumber: firstRow?.vendorNumber,
          },
        ]);
        setSelectedVendorId(String(firstRow?.vendorId));
      }
      if (firstRow?.jobNumber) {
        isPrefilled.current = true;
        setHasExistingRecurring(true);
        setJobNumberError('');
      }
    }
  }, [selectedRows, setValue, open]);

  useEffect(() => {
    if (!open) return;
    if (jobNumber?.length < DEFAULT_JOB_NUMBER_LENGTH) {
      setJobNumberError('');
    }
  }, [jobNumber, open]);

  useEffect(() => {
    if (!open) return;
    if (isPrefilled.current) return;
    const jobNumberLength = jobNumber ? jobNumber?.length : 0;
    if (
      jobNumberLength >= DEFAULT_JOB_NUMBER_LENGTH &&
      jobNumberLength <= DEFAULT_JOB_NUMBER_MAX_LENGTH &&
      !jobNumberTimerRef.current
    ) {
      fetchExistingJobNumber(String(jobNumber || ''));
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      const firstRow = selectedRows?.[0];
      reset({
        [PeriodicJobFormFields.JobNumber]: firstRow?.jobNumber || '',
        [PeriodicJobFormFields.FulfillmentType]: getInitialFulfillmentType(firstRow),
        [PeriodicJobFormFields.Vendor]: getFormattedVendor(firstRow),
      });
      setJobNumberError('');
      isPrefilled.current = false;
      if (firstRow?.vendorId) {
        setSelectedVendorData([
          {
            vendorId: firstRow?.vendorId,
            vendorName: firstRow?.vendorName,
            vendorNumber: firstRow?.vendorNumber,
          },
        ]);
        setSelectedVendorId(String(firstRow?.vendorId));
      } else {
        setSelectedVendorData([]);
      }
    }
  }, [open, reset, selectedRows]);

  const disableConfirmButton = () => {
    const baseDisabled =
      !jobNumber || !fulfillmentType || (fulfillmentType === JobFulfillmentType.SubContracted && !vendor);
    const length = jobNumber ? jobNumber?.length : 0;
    const requiresRecurring = length >= DEFAULT_JOB_NUMBER_LENGTH && length <= DEFAULT_JOB_NUMBER_MAX_LENGTH;
    const recurringOk = requiresRecurring ? hasExistingRecurring : true;
    return baseDisabled || isJobNumberLoading || !recurringOk;
  };

  return (
    <>
      <DialogWrapper
        open={open}
        onClose={onClose}
        PaperProps={{
          style: { width: '40rem', maxWidth: '40rem' },
        }}>
        <CloseDialogBoxIcon onClick={onClose}>
          <img src={Icons.InfoWindowCloseIcon} alt={t('altTexts:closeIcon')} />
        </CloseDialogBoxIcon>
        <CardTitle>{t('jobManagement:createPeriodicJob')}</CardTitle>
        <DatePickerContainer>
          <Controller
            control={control}
            name={PeriodicJobFormFields.JobNumber}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label={
                  <span>
                    {t('vendor:jobNumber')}
                    <span className="mandatory-field">*</span>
                  </span>
                }
                InputLabelProps={{ sx: { fontSize: '0.9rem' } }}
                value={value}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, '');
                  const truncated = digitsOnly.slice(0, 5);
                  onChange(truncated);
                  isPrefilled.current = false;
                  setHasExistingRecurring(false);

                  if (jobNumberTimerRef.current) window.clearTimeout(jobNumberTimerRef.current);
                  if (
                    truncated.length >= DEFAULT_JOB_NUMBER_LENGTH &&
                    truncated.length <= DEFAULT_JOB_NUMBER_MAX_LENGTH
                  ) {
                    jobNumberTimerRef.current = window.setTimeout(() => {
                      fetchExistingJobNumber(truncated);
                      jobNumberTimerRef.current = null;
                    }, DebounceTime);
                  } else {
                    setJobNumberError('');
                    setHasExistingRecurring(false);
                  }
                }}
                sx={{ ...textField, width: '100%' }}
                helperText={
                  error?.message
                    ? error?.message
                    : value && value?.length < DEFAULT_JOB_NUMBER_LENGTH
                      ? t('jobManagement:invalidJobNumber')
                      : jobNumberError
                }
                FormHelperTextProps={{
                  sx: { color: 'error.main' },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {isJobNumberLoading ? (
                        <CircularProgress size={25} sx={{ color: theme.palette.primary.dark }} />
                      ) : (
                        value && (
                          <img
                            src={Icons.CancelIcon}
                            onClick={() => {
                              onChange('');
                              isPrefilled.current = false;
                              setHasExistingRecurring(false);
                              setJobNumberError('');
                            }}
                            style={{ cursor: 'pointer' }}
                            alt="clear"
                          />
                        )
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </DatePickerContainer>

        <DatePickerContainer>
          <Controller
            control={control}
            name={PeriodicJobFormFields.FulfillmentType}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                InputLabelProps={{ sx: { fontSize: '0.9rem' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setFulfillmentTypeOpen(!isFulfillmentTypeOpen)} style={{ padding: 0 }}>
                        <img
                          src={isFulfillmentTypeOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                          alt={isFulfillmentTypeOpen ? 'down' : 'up'}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                SelectProps={{
                  open: isFulfillmentTypeOpen,
                  IconComponent: () => null,
                  onOpen: () => setFulfillmentTypeOpen(true),
                  onClose: () => setFulfillmentTypeOpen(false),
                  MenuProps: {
                    PaperProps: {
                      style: { boxShadow: BoxShadow.BoxShadow },
                    },
                  },
                }}
                label={
                  <span>
                    {t('tableHeader:fulfillmentType')}
                    <span className="mandatory-field">*</span>
                  </span>
                }
                sx={{ ...textField, width: '100%' }}
                onChange={(e) => {
                  field.onChange(e);
                  const value = e?.target?.value as JobFulfillmentType;
                  if (value === JobFulfillmentType.SelfPerformed) {
                    setSelectedVendorData([
                      {
                        vendorId: velocitiOrganizationId,
                        vendorName: VELOCITI_VENDOR_NAME,
                        vendorNumber: VELOCITI_NUMBER,
                      },
                    ]);
                    setSelectedVendorId(velocitiOrganizationId);
                    setValue(PeriodicJobFormFields.Vendor, `${VELOCITI_NUMBER} - ${VELOCITI_VENDOR_NAME}`, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  } else {
                    setSelectedVendorData([]);
                    setSelectedVendorId(undefined);
                    setValue(PeriodicJobFormFields.Vendor, '', {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }
                }}>
                {Object.values(JobFulfillmentType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </DatePickerContainer>

        <DatePickerContainer>
          <Controller
            control={control}
            name={PeriodicJobFormFields.Vendor}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('jobManagement:vendor')}
                InputLabelProps={{ sx: { fontSize: '0.9rem' } }}
                placeholder={
                  fulfillmentType === JobFulfillmentType.SubContracted
                    ? t('jobManagement:searchVendor')
                    : t('jobManagement:selectVendor')
                }
                sx={{
                  ...textField,
                  width: '100%',
                  cursor: fulfillmentType === JobFulfillmentType.SubContracted ? 'pointer' : 'default',
                }}
                disabled={fulfillmentType !== JobFulfillmentType.SubContracted}
                onClick={() => {
                  if (fulfillmentType === JobFulfillmentType.SubContracted && !field.value) {
                    handleOpenVendorSelection();
                  }
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        disabled={fulfillmentType !== JobFulfillmentType.SubContracted}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenVendorSelection();
                        }}
                        edge="end">
                        <img
                          src={Icons.EditBlackIcon}
                          alt="edit"
                          style={{
                            opacity: fulfillmentType === JobFulfillmentType.SubContracted ? 1 : 0.5,
                          }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </DatePickerContainer>

        <Button
          sx={{
            ...confirmButton,
            opacity: disableConfirmButton() || isLoading || isCreatingJob ? 0.5 : 1,
          }}
          disabled={disableConfirmButton() || isLoading || isCreatingJob}
          onClick={handleSubmit(fetchPeriodicJobs)}>
          {isCreatingJob || isLoading ? (
            <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
          ) : (
            <Typography sx={{ ...buttonText, color: theme.palette.common.white }}>
              {t('jobManagement:createPeriodicJob')}
            </Typography>
          )}
        </Button>
      </DialogWrapper>
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default CreatePeriodicJobCard;
